/**
 * ElectronicPowerBadge
 *
 * Renders a compact "Electronic" indicator next to a power number.
 *
 * SOURCE OF TRUTH: the ElectronicPowerLink table, surfaced by the API as
 * `isElectronicPower` (boolean) on each power row. Pass the row as `power`
 * (or the boolean as `isElectronic`) and the badge renders off that.
 * Optional `electronicPowerTypeDescription` / `electronicPowerTypeCd` on the
 * row feed the tooltip; a null type is normal (ranges are still unclassified)
 * and never means "not electronic".
 *
 * FALLBACK (transitional): when the row carries no `isElectronicPower` at
 * all — i.e. the endpoint hasn't been updated yet — the badge falls back to
 * the old inference: does the number fall inside one of the agency's
 * ElectronicPowerInventory ranges? That inference over-reports (a power can
 * sit inside a range without ever having been linked), so it is gated by
 * ELECTRONIC_RANGE_FALLBACK and should be switched off once every Oath
 * endpoint returns the flag.
 *
 * Renders nothing (null) when the power isn't electronic, or when the
 * fallback is in use and ranges haven't loaded / the number isn't numeric.
 *
 * Props:
 *   - power        (object)            the API row; read for isElectronicPower + type
 *   - isElectronic (boolean)           explicit override of the row flag
 *   - powerNumber  (string | number)   used only by the range fallback
 *   - agencyId     (number | string)   used only by the range fallback
 *   - size         'sm' (default) | 'xs' | 'icon' | 'lg' — icon only, no label
 */

import React from 'react';
import { BoltIcon } from '@heroicons/react/24/solid';
import { useElectronicRanges } from '../../services/useElectronicRanges';

// Flip to false once the Oath API returns isElectronicPower on every power
// row the portal renders. Until then rows without the field use range
// inference so nothing regresses.
export const ELECTRONIC_RANGE_FALLBACK = true;

export const ELECTRONIC_POWER_FLAG_FIELD = 'isElectronicPower';

// true / false when the row carries the link-table flag, undefined otherwise.
export function readElectronicFlag(power, override) {
  if (typeof override === 'boolean') return override;
  const v = power?.[ELECTRONIC_POWER_FLAG_FIELD];
  return typeof v === 'boolean' ? v : undefined;
}

export function isElectronicPowerRow(power) {
  return readElectronicFlag(power) === true;
}

// Some write responses (rewrite draft/submitted, recalculate) build their
// power rows through a mapper that does not join ElectronicPowerLink and so
// report isElectronicPower: false for every row. When such a response
// replaces rows we already hold, carry the electronic fields forward from
// the row we had. Match on powerId, falling back to powerNumber / powerNo.
const ELECTRONIC_FIELDS = ['isElectronicPower', 'electronicPowerTypeCd', 'electronicPowerTypeDescription'];
function rowKey(r) {
  return r?.powerId ?? r?.powerNumber ?? r?.powerNo ?? null;
}
export function preserveElectronicFlags(previousRows, nextRows) {
  if (!Array.isArray(nextRows)) return nextRows;
  if (!Array.isArray(previousRows) || previousRows.length === 0) return nextRows;
  const prevByKey = new Map();
  for (const r of previousRows) {
    const k = rowKey(r);
    if (k != null && readElectronicFlag(r) === true) prevByKey.set(String(k), r);
  }
  if (prevByKey.size === 0) return nextRows;
  return nextRows.map((r) => {
    if (readElectronicFlag(r) === true) return r;
    const prev = prevByKey.get(String(rowKey(r)));
    if (!prev) return r;
    const out = { ...r };
    for (const f of ELECTRONIC_FIELDS) out[f] = prev[f];
    return out;
  });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDenom(desc) {
  const n = parseFloat(desc);
  if (!isFinite(n) || n <= 0) return null;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 2)}M`;
  return `$${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
}

function formatExpiration(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

// Sizing per variant. Icon-only everywhere — the bolt is the indicator, the
// tooltip / aria-label carry the words. Variants only change footprint.
const VARIANTS = {
  sm:   { badge: 'h-4 w-4',   icon: 'h-3 w-3' },
  xs:   { badge: 'h-3.5 w-3.5', icon: 'h-2.5 w-2.5' },
  icon: { badge: 'h-4 w-4',   icon: 'h-3 w-3' },
  lg:   { badge: 'h-5 w-5',   icon: 'h-3.5 w-3.5' },
};

/**
 * ElectronicPowerCount — "N electronic" pill for receipt order rows.
 * Reads the API's electronicPowerCount (authoritative — never recomputed
 * client-side). Renders nothing when the count is 0 or absent.
 */
export function ElectronicPowerCount({ count, total, className = '' }) {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return null;
  const t = Number(total);
  const title = Number.isFinite(t) && t > 0
    ? (n >= t ? `All ${t} powers electronic` : `${n} of ${t} powers electronic`)
    : `${n} electronic power${n === 1 ? '' : 's'}`;
  return (
    <span
      className={
        'inline-flex items-center gap-0.5 rounded-full pl-0.5 pr-1.5 py-px text-[10px] leading-none font-semibold whitespace-nowrap ' +
        'bg-primary/10 text-primary dark:text-primary-light ring-1 ring-inset ring-primary/25 dark:ring-primary-light/30 ' +
        className
      }
      title={title}
      aria-label={title}
      data-testid="electronic-power-count"
    >
      <span className="inline-flex items-center justify-center rounded-full bg-primary text-white h-3 w-3" aria-hidden="true">
        <BoltIcon className="h-2.5 w-2.5" />
      </span>
      <span>{n}</span>
    </span>
  );
}

export default function ElectronicPowerBadge({ power, isElectronic, powerNumber, agencyId, size = 'sm' }) {
  const flag = readElectronicFlag(power, isElectronic);
  // Only hit the ranges cache when the row gives us nothing to go on and the
  // transitional fallback is still enabled. Passing null skips the fetch.
  const useFallback = flag === undefined && ELECTRONIC_RANGE_FALLBACK;
  const { isElectronic: rangeMatch } = useElectronicRanges(useFallback ? agencyId : null);

  let tooltipParts;
  if (flag === true) {
    tooltipParts = ['Electronic'];
    const type = power?.electronicPowerTypeDescription || power?.electronicPowerTypeCd;
    if (type) tooltipParts.push(type);
    if (power?.electronicPowerExternalPlatform) tooltipParts.push(`via ${power.electronicPowerExternalPlatform}`);
  } else if (flag === false || !useFallback) {
    return null;
  } else {
    const match = rangeMatch(powerNumber);
    if (!match) return null;
    tooltipParts = ['Electronic'];
    const denom = formatDenom(match.powerAmountDescription);
    const exp = formatExpiration(match.powerExpirationDate);
    if (denom) tooltipParts.push(denom);
    if (exp) tooltipParts.push(`Expires ${exp}`);
  }

  const v = VARIANTS[size] || VARIANTS.sm;

  return (
    <span
      className={`inline-flex items-center justify-center rounded-full bg-primary text-white shrink-0 ${v.badge}`}
      title={tooltipParts.join(' • ')}
      aria-label={tooltipParts.join(', ')}
      role="img"
      data-testid="electronic-power-badge"
    >
      <BoltIcon className={v.icon} aria-hidden="true" />
    </span>
  );
}
